import { useState, useEffect } from 'react';
import {
  TrendingUp, Users, MessageSquare, CreditCard,
  RefreshCw, Loader2,
  BarChart3, Sparkles, AlertCircle, Clock, Rss
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie,
  AreaChart, Area, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { analyticsService } from '../../services/PlatformService';
import { getAgentAvatar } from '../../lib/formatters';

// Cores do design system
const COLORS = {
  primary: '#A020F0',
  secondary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  channels: {
    whatsapp: '#25D366',
    whatsapp_qr: '#25D366',
    whatsapp: '#25D366',
    instagram_direct: '#A020F0',
    platform: '#3B82F6',
    messenger: '#4882deff',
    integration: '#FF6B35',
    whatsapp_business: '#25D366',
    kommo_crm: '#5B3FFF'
  }
};

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState(30);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [agentsRanking, setAgentsRanking] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [dashboard, ranking] = await Promise.all([
        analyticsService.getDashboard(period),
        analyticsService.getAgentsRanking(period)
      ]);
      
      setDashboardData(dashboard.data);
      setAgentsRanking(ranking.data || []);
      
      // Se período for 7 dias, buscar dados por hora
      if (period === 7) {
        const heatmap = await analyticsService.getHourlyHeatmap(period);
        setHourlyData(heatmap.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      setError('Erro ao carregar dados. Tente novamente.');
      
      // Definir dados vazios em caso de erro
      setDashboardData({
        metricas_principais: {
          total_atendimentos: 0,
          total_mensagens: 0,
          mensagens_estimadas: 0,
          taxa_resposta: 0,
          funcionarios_ativos: 0
        },
        custos: {
          total: 0,
          economia_estimada: 0
        },
        distribuicao_canais: {},
        timeline: []
      });
      setAgentsRanking([]);
      setHourlyData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('pt-BR').format(num || 0);
  };

  // Preparar dados para o gráfico de pizza
  const pieData = dashboardData?.distribuicao_canais 
    ? Object.entries(dashboardData.distribuicao_canais)
        .filter(([_, value]) => value > 0)
        .map(([channel, value]) => ({
          name: channel === 'whatsapp' || channel === 'whatsapp_business' ? 'WhatsApp' : 
                channel === 'instagram_direct' ? 'Instagram Direct' : 
                channel === 'messenger' ? 'Facebook Messenger' :
                channel === 'platform' ? 'Plataforma' : 
                channel === 'integration' ? 'Integrações' :
                channel === 'kommo_crm' ? 'Kommo CRM' : channel,
          value,
          color: COLORS.channels[channel] || '#6B7280'
        }))
    : [];

  // Custom Tooltip para os gráficos
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200">
          <p className="font-semibold text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm mt-1">
              <span style={{ color: entry.color }}>{entry.name}:</span>{' '}
              <span className="font-medium">
                {entry.name.includes('Custo') || entry.name.includes('custo') 
                  ? formatCurrency(entry.value) 
                  : formatNumber(entry.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Label para o gráfico de pizza
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Não mostrar label se menos de 5%

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="font-semibold text-sm"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-600">Carregando analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header com Filtros */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-green-600" />
            Painel de Analytics
          </h3>
            <p className="text-lg text-gray-600 mt-1">
              Acompanhe o desempenho da sua equipe digital
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="px-6 py-4 text-lg font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-2xl hover:bg-gray-50 focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all cursor-pointer"
            >
              <option value={7}>Últimos 7 dias</option>
              <option value={15}>Últimos 15 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
            
            <button
              onClick={fetchDashboardData}
              className="p-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all"
              title="Atualizar dados"
            >
              <RefreshCw className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total de Atendimentos */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base text-gray-600 mb-1">Total de Atendimentos</p>
              <h3 className="text-3xl font-bold text-gray-900">
                {formatNumber(dashboardData?.metricas_principais?.total_atendimentos || 0)}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                nos últimos {period} dias
              </p>
            </div>
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-green-100 rounded-2xl">
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Mensagens Trocadas */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base text-gray-600 mb-1">Mensagens Trocadas</p>
              <h3 className="text-3xl font-bold text-gray-900">
                {formatNumber(dashboardData?.metricas_principais?.total_mensagens || 0)}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Taxa de resposta: {dashboardData?.metricas_principais?.taxa_resposta || 0}%
              </p>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl">
              <MessageSquare className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Custo Operacional */}
<div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02]">
  <div className="flex items-start justify-between">
    <div>
      <p className="text-base text-gray-600 mb-1">Tokens Utilizados</p>
      <h3 className="text-3xl font-bold text-gray-900">
        {formatNumber(dashboardData?.tokens?.total || 0)}
      </h3>
      <p className="text-sm text-gray-500 mt-2">
        uso de tokens no período
      </p>
    </div>
    <div className="p-3 bg-gradient-to-br from-gray-100 to-slate-100 rounded-2xl">
      <CreditCard className="w-8 h-8 text-gray-600" />
    </div>
  </div>
</div>

        {/* Funcionários Ativos */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base text-gray-600 mb-1">Funcionários Ativos</p>
              <h3 className="text-3xl font-bold text-gray-900">
                {dashboardData?.metricas_principais?.funcionarios_ativos || 0}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Trabalhando 24/7
              </p>
            </div>
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-pink-100 rounded-2xl">
              <Sparkles className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Volume de Atendimento */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            {period === 7 ? <Clock className="w-6 h-6 text-green-600" /> : <TrendingUp className="w-6 h-6 text-green-600" />}
            {period === 7 ? 'Distribuição por Horário' : 'Volume de Atendimento'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            {period === 7 && hourlyData.length > 0 ? (
              // Gráfico de barras por hora quando período é 7 dias
              <BarChart data={hourlyData}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.9}/>
                    <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.7}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="periodo" 
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis style={{ fontSize: '14px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="atendimentos"
                  fill="url(#barGradient)"
                  radius={[8, 8, 0, 0]}
                  name="Atendimentos"
                />
              </BarChart>
            ) : (
              // Gráfico de área para outros períodos
<AreaChart data={dashboardData?.timeline || []}>
  <defs>
    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
      <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.1}/>
    </linearGradient>
  </defs>
  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
  <XAxis 
    dataKey="date" 
    tickFormatter={(date) => {
      // Parse da data no formato YYYY-MM-DD
      const [year, month, day] = date.split('-');
      return `${day}/${month}`;
    }}
    style={{ fontSize: '14px' }}
  />
  <YAxis style={{ fontSize: '14px' }} />
  <Tooltip 
    content={({ active, payload, label }) => {
      if (active && payload && payload.length && label) {
        // Parse da data no formato YYYY-MM-DD
        const [year, month, day] = label.split('-');
        const meses = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const mesNome = meses[parseInt(month)] || month;
        
        return (
          <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200">
            <p className="font-semibold text-gray-900">
              {day} de {mesNome} de {year}
            </p>
            {payload.map((entry, index) => (
              <p key={index} className="text-sm mt-1">
                <span style={{ color: entry.color }}>{entry.name}:</span>{' '}
                <span className="font-medium">
                  {formatNumber(entry.value)}
                </span>
              </p>
            ))}
          </div>
        );
      }
      return null;
    }}
  />
  <Area
    type="monotone"
    dataKey="mensagens_estimadas"
    stroke={COLORS.primary}
    strokeWidth={3}
    fillOpacity={1}
    fill="url(#colorGradient)"
    name="Mensagens"
  />
</AreaChart>
            )}
          </ResponsiveContainer>
          {period === 7 && hourlyData.length > 0 && (
            <p className="text-sm text-gray-600 mt-4 text-center">
              💡 Horários com mais atendimentos indicam os melhores momentos para campanhas
            </p>
          )}
        </div>

        {/* Distribuição por Canal */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Rss className="w-6 h-6 text-green-600" />
            Distribuição por Canal
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              <p>Sem dados de distribuição no período</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-4 justify-center">
            {pieData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-700">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ranking de Funcionários */}
      {agentsRanking.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-green-600" />
            Top Funcionários do Período
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agentsRanking.map((agent, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-4 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-3xl font-bold text-green-600">
                    #{index + 1}
                  </div>
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-indigo-100 to-green-100 p-1">
                    <div className="w-full h-full rounded-full overflow-hidden bg-white">
                      <img
                        src={getAgentAvatar(agent)}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{agent.name}</h4>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Atendimentos:</span>
                    <span className="font-semibold">{formatNumber(agent.total_atendimentos || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Mensagens:</span>
                    <span className="font-semibold">{formatNumber(agent.total_mensagens || 0)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default AnalyticsDashboard;